/**
 * ============================================================
 * Bulgaria Compliance Detection
 * ============================================================
 * Versie:   1.0
 * Doel:     Detecteert bezoekers uit Bulgarije via meerdere
 *           onafhankelijke checks (OR-logica).
 * 
 * Zodra EEN check Bulgarije detecteert, wordt het element
 * met id="bg-compliance-info" zichtbaar gemaakt.
 * 
 * Checks (elk kan onafhankelijk triggeren):
 *   1. Shopify Markets (server-side, via Liquid)
 *   2. Browser-taal (navigator.languages)
 *   3. Timezone (Intl API)
 *   4. Locale (Shopify request.locale)
 *   5. Referrer (.bg domein)
 *   6. Geolocatie-API 1 (ipapi.co)
 *   7. Geolocatie-API 2 (ipwho.is)
 *   8. VPN-detectie + BG-signaal combinatie
 * ============================================================
 */

(function() {
  'use strict';

  // ============ CONFIGURATIE ============
  const CONFIG = {
    targetCountry: 'BG',
    targetLanguagePrefix: 'bg',
    targetTimezones: ['Europe/Sofia'],
    targetDomainSuffix: '.bg',
    elementSelector: '[data-bg-compliance]',
    geoAPIs: [
      { url: 'https://ipapi.co/json/', countryField: 'country_code', vpnField: 'proxy' },
      { url: 'https://ipwho.is/', countryField: 'country_code', vpnField: null }
    ],
    apiTimeoutMs: 2500,
    debug: false // zet op true om checks in console te zien
  };

  // ============ HELPERS ============
  function log(...args) {
    if (CONFIG.debug) console.log('[BG-Compliance]', ...args);
  }

function showBGInfo() {
    const elements = document.querySelectorAll(CONFIG.elementSelector);
    elements.forEach(el => { el.style.display = 'block'; });

    // Als we op een policy-pagina zitten, injecteer het blok automatisch
    if (window.location.pathname.includes('/policies/')) {
      const policyContainer = document.querySelector('.shopify-policy__container, .policy-container, .rte');
      if (policyContainer) {
        const existing = policyContainer.querySelector('[data-bg-compliance]');
        if (!existing) {
          const div = document.createElement('div');
          div.setAttribute('data-bg-compliance', '');
          div.id = 'bg-compliance-info-policy';
          div.style.cssText = 'margin-top: 2rem; padding: 1rem; border-top: 1px solid #e0e0e0; font-size: 0.875rem; line-height: 1.6;';
          div.innerHTML = '<h3 style="margin-top:0; font-size:1rem; font-weight:600;">Company Information</h3>' +
            '<p style="margin:0.25rem 0;"><strong>Company:</strong> GT ECOM B.V.</p>' +
            '<p style="margin:0.25rem 0;"><strong>KVK Number:</strong> 82232393</p>' +
            '<p style="margin:0.25rem 0;"><strong>Address:</strong> Betuwehaven 8, 3433PV Nieuwegein</p>' +
            '<p style="margin:0.25rem 0;"><strong>VAT Number:</strong> NL862386780B01</p>';
          policyContainer.appendChild(div);
          log('Policy-blok geïnjecteerd');
        }
      }
    }

    log('Bulgaarse info getoond');
  }

  function fetchWithTimeout(url, ms) {
    return Promise.race([
      fetch(url).then(r => r.json()),
      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms))
    ]);
  }

  // ============ DETECTIE-CHECKS ============

  // Check 1: Shopify Markets (al uitgevoerd door Liquid)
  function checkShopifyMarkets() {
    const market = (window.__bgCompliance && window.__bgCompliance.shopifyMarket) || '';
    const match = market.toUpperCase() === CONFIG.targetCountry;
    log('Check 1 (Shopify Markets):', market, '→', match);
    return match;
  }

  // Check 2: Browser-taal
  function checkBrowserLanguage() {
    const languages = navigator.languages || [navigator.language || ''];
    const match = languages.some(l => 
      l.toLowerCase().startsWith(CONFIG.targetLanguagePrefix)
    );
    log('Check 2 (Browser-taal):', languages, '→', match);
    return match;
  }

  // Check 3: Timezone
  function checkTimezone() {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const match = CONFIG.targetTimezones.includes(tz);
      log('Check 3 (Timezone):', tz, '→', match);
      return match;
    } catch (e) {
      log('Check 3 (Timezone): fout', e);
      return false;
    }
  }

  // Check 4: Shopify locale
  function checkShopifyLocale() {
    const locale = (window.__bgCompliance && window.__bgCompliance.locale) || '';
    const match = locale.toLowerCase().startsWith(CONFIG.targetLanguagePrefix);
    log('Check 4 (Shopify locale):', locale, '→', match);
    return match;
  }

  // Check 5: Referrer
  function checkReferrer() {
    const ref = document.referrer || '';
    let match = false;
    try {
      if (ref) {
        const hostname = new URL(ref).hostname;
        match = hostname.endsWith(CONFIG.targetDomainSuffix);
      }
    } catch (e) {}
    log('Check 5 (Referrer):', ref, '→', match);
    return match;
  }

  // Check 6 & 7 & 8: Geolocatie-APIs + VPN-detectie
  async function checkGeolocationAPIs(otherSignalsPositive) {
    const results = await Promise.allSettled(
      CONFIG.geoAPIs.map(api => 
        fetchWithTimeout(api.url, CONFIG.apiTimeoutMs)
          .then(data => ({ data, api }))
      )
    );

    let countryMatch = false;
    let vpnWithBGSignal = false;

    for (const result of results) {
      if (result.status !== 'fulfilled') continue;
      const { data, api } = result.value;
      const country = (data[api.countryField] || '').toUpperCase();
      const isVPN = api.vpnField ? Boolean(data[api.vpnField] || data.hosting) : false;

      log('Geo API', api.url, '→ land:', country, 'VPN:', isVPN);

      if (country === CONFIG.targetCountry) countryMatch = true;
      if (isVPN && otherSignalsPositive) vpnWithBGSignal = true;
    }

    return { countryMatch, vpnWithBGSignal };
  }

  // ============ HOOFDLOGICA (OR) ============
  async function detectBulgaria() {
    // Eerst alle snelle synchrone checks
    const quickChecks = [
      checkShopifyMarkets(),
      checkBrowserLanguage(),
      checkTimezone(),
      checkShopifyLocale(),
      checkReferrer()
    ];

    const anyQuickPositive = quickChecks.some(Boolean);

    // Als een snelle check al hit geeft → direct tonen, APIs niet meer nodig
    if (anyQuickPositive) {
      log('Quick check positief, BG-info tonen');
      showBGInfo();
      return;
    }

    // Anders: async API-checks doen (inclusief VPN-combinatie)
    const browserSignals = checkBrowserLanguage() || checkTimezone() || checkShopifyLocale();
    try {
      const { countryMatch, vpnWithBGSignal } = await checkGeolocationAPIs(browserSignals);
      if (countryMatch || vpnWithBGSignal) {
        log('Geo/VPN check positief, BG-info tonen');
        showBGInfo();
      } else {
        log('Geen BG-signalen gevonden, niets tonen');
      }
    } catch (e) {
      log('Geo API error:', e);
    }
  }

  // ============ INIT ============
  function init() {
    // Als Liquid al heeft getoond (Shopify Markets = BG), hoeven we niets te doen
    const el = document.querySelector(CONFIG.elementSelector);
    if (el && el.style.display === 'block') {
      log('Al getoond door Liquid, skip JS-detectie');
      return;
    }
    detectBulgaria();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();